import React from "react";
import classnames from "classnames";
import { withApollo } from "@apollo/client/react/hoc";
import { get } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import {
  EmployerApplicationApproved,
  IndividualContributionPending,
  IndividualInvestmentProfileUpdate,
  IndividualLoginCreated,
  IndividualPortfolioPending,
  IndividualPortfolioUpdate,
  IndividualRequiredUpdatesPending,
  lifecycleStates,
  onboardingStates,
} from "statics/states";
import { adminSite, employerSite, individualSite } from "utils/determineSite";
import { FiLock } from "react-icons/fi";
import NavBar from "components/navbar/NavBar";
import type { RootState } from "types/state";

// Props a parent passes in directly (propTypes minus redux-injected props
// minus the withApollo-injected `client`).
interface IconLayoutOwnProps {
  routes?: React.ReactNode;
  isSmallScreen?: boolean;
}

const mapStateToProps = (state: RootState, ownProps: IconLayoutOwnProps) => {
  // `state.user` carries an index signature of `unknown`, so lodash `get`
  // widens to `unknown` here; cast back to the string these paths hold at
  // runtime (matches the original `userState: string` propType).
  const userState = get(state.user, "userState.state", "") as string;

  const onBoardingStates = [
    ...onboardingStates,
    IndividualLoginCreated,
    IndividualPortfolioPending,
    IndividualContributionPending,
    IndividualInvestmentProfileUpdate,
    IndividualPortfolioUpdate,
    IndividualRequiredUpdatesPending,
  ];

  return {
    // pre-existing quirk: mapStateToProps spreads the parent's ownProps back
    // out under a literal `ownProps` key rather than merging them flat
    ownProps,
    // same boundary cast as userState — runtime value is the company state
    // string (original `companyState: string` propType).
    companyState: get(state.user, "companyState.state") as string,
    isIndividualSubNav: lifecycleStates.includes(userState) && individualSite(),
    userState,
    userIsOnboarding: onBoardingStates.includes(userState),
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type IconLayoutProps = PropsFromRedux & IconLayoutOwnProps;

// state is always the empty object literal here — never written via setState.
type IconLayoutState = Record<string, never>;

class IconLayout extends React.PureComponent<IconLayoutProps, IconLayoutState> {
  constructor(props: IconLayoutProps) {
    super(props);

    this.state = {};
  }

  styleSheetId = (): string | undefined => {
    if (employerSite()) {
      return "employer-portal";
    } else if (individualSite()) {
      return "individual-portal";
    } else if (adminSite()) {
      return "admin-portal";
    }
  };

  render() {
    const isEmployerDashboard = [EmployerApplicationApproved].includes(
      this.props.companyState
    );

    return (
      <div id="icon-layout">
        <main className="main-content" role="main" id={this.styleSheetId()}>
          <div className="max-container">
            <NavBar />
          </div>
          <div className="max-container main-body">
            <div
              className={classnames({
                container: !adminSite(),
              })}
            >
              <div
                className={classnames("padding", {
                  ["remove-padding"]: isEmployerDashboard,
                })}
              >
                <div
                  className={classnames("body-content-wrap", {
                    ["subnav-body-content-wrap"]:
                      this.props.isIndividualSubNav &&
                      !this.props.isSmallScreen,
                  })}
                >
                  {this.props.routes}
                </div>
              </div>
            </div>
          </div>
        </main>
        {this.props.userIsOnboarding && (
          <div className="secure-site-banner">
            <div className="secure-site-banner-content">
              <FiLock color="white" stroke="#5e6b75" size={16} />
              <p>Your information is always secure.</p>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default connector(withApollo<IconLayoutProps>(IconLayout));
